import {typeboxResolver} from '@hookform/resolvers/typebox'
import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {FieldError, Form, Input, Label, TextField} from 'react-aria-components'
import {Controller, type SubmitHandler, useForm} from 'react-hook-form'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {updateName} from '@/services/authenticationService'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import style from '@/views/Account/Profile/Profile.module.scss'

const schema = Type.Object({
  firstName: Type.String({
    minLength: 1,
    default: '',
    errorMessage: "First name shouldn't be left empty",
  }),
  lastName: Type.String({
    minLength: 1,
    default: '',
    errorMessage: "Last name shouldn't be left empty",
  }),
})

const typecheck = TypeCompiler.Compile(schema)

type Schema = Static<typeof schema>

export default memo(function ChangeInformation() {
  const [isUpdating, setIsUpdating] = useState(false)
  const [isUpdated, setIsUpdated] = useState(false)

  const accountInformation = useAccountInformationValue()
  const [firstName, lastName] = accountInformation?.accountName.split(' ') ?? ['', '']

  const {handleSubmit, control, reset, formState} = useForm<Schema>({
    mode: 'onTouched',
    resolver: typeboxResolver(typecheck),
    defaultValues: {
      firstName,
      lastName,
    },
  })

  if (formState.isDirty && isUpdated) {
    setIsUpdated(false)
  }

  const onSubmit: SubmitHandler<Schema> = data => {
    setIsUpdating(true)
    void updateName({
      firstName: data.firstName,
      lastName: data.lastName,
    })
      .then(result => {
        if (result) {
          if (typeof result === 'string') {
            toastErrorMessage(result)
          } else {
            setIsUpdated(true)
            reset()
          }
        } else {
          toastErrorMessage()
        }
      })
      .finally(() => {
        setIsUpdating(false)
      })
  }
  return (
    <>
      <h2>Information</h2>
      <Form className={clsx(style.Form)} onSubmit={handleSubmit(onSubmit)}>
        <TextField
          name={'email'}
          value={accountInformation?.email}
          validationBehavior='aria'
          isInvalid={false}
          isRequired
          isReadOnly
          type='text'
          className={style.Field}
        >
          <Label className={style.Label}>Email</Label>
          <Input className={style.Input} />
        </TextField>
        <div className={style.RowFieldGroup}>
          <Controller
            control={control}
            name='firstName'
            render={({
              field: {name, value, onChange, onBlur, ref},
              fieldState: {invalid, error},
            }) => (
              <TextField
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                validationBehavior='aria'
                isInvalid={invalid}
                isRequired
                type='text'
                className={style.Field}
              >
                <Label className={style.Label}>First name</Label>
                <Input className={style.Input} ref={ref} />
                {error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name='lastName'
            render={({
              field: {name, value, onChange, onBlur, ref},
              fieldState: {invalid, error},
            }) => (
              <TextField
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                validationBehavior='aria'
                isInvalid={invalid}
                isRequired
                type='text'
                className={style.Field}
              >
                <Label className={style.Label}>Last name</Label>
                <Input className={style.Input} ref={ref} />
                {error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
              </TextField>
            )}
          />
        </div>
        <div className={style.SubmitArea}>
          <PrimaryButton type='submit' isPending={isUpdating}>
            {(() => {
              if (isUpdating) {
                return 'Saving...'
              }
              if (isUpdated) {
                return 'Saved'
              }
              return 'Save change'
            })()}
          </PrimaryButton>
        </div>
      </Form>
    </>
  )
})
